import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Plus, X, ExternalLink } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCreateUserProfile } from '../hooks/useQueries';
import { SocialLink } from '../types';

const platformOptions = [
  { id: 'instagram', name: 'Instagram', icon: '📷', placeholder: 'https://instagram.com/yourusername' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', placeholder: 'https://tiktok.com/@yourusername' },
  { id: 'shopee', name: 'Shopee', icon: '🛒', placeholder: 'https://shopee.co.id/yourusername' },
  { id: 'facebook', name: 'Facebook', icon: '👥', placeholder: 'https://facebook.com/yourusername' },
  { id: 'x', name: 'X (Twitter)', icon: '🐦', placeholder: 'https://x.com/yourusername' },
  { id: 'threads', name: 'Threads', icon: '🧵', placeholder: 'https://threads.net/@yourusername' },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const createUserProfileMutation = useCreateUserProfile();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  
  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || '',
    bio: '',
    avatar: ''
  });
  
  const [socialLinks, setSocialLinks] = useState<Omit<SocialLink, 'id'>[]>([]);

  const addSocialLink = (platform: string) => {
    const platformConfig = platformOptions.find(p => p.id === platform);
    if (!platformConfig) return;

    setSocialLinks(prev => [...prev, {
      platform: platform as SocialLink['platform'],
      url: '',
      title: platformConfig.name,
      isActive: true
    }]);
  };

  const updateSocialLink = (index: number, field: keyof Omit<SocialLink, 'id'>, value: string | boolean) => {
    setSocialLinks(prev => prev.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    ));
  };

  const removeSocialLink = (index: number) => {
    setSocialLinks(prev => prev.filter((_, i) => i !== index));
  };

  const handleComplete = async () => {
    if (!user) return;
    
    try {
      const socialLinksWithIds = socialLinks.map((link, index) => ({
        ...link,
        id: `link-${Date.now()}-${index}`
      }));

      await createUserProfileMutation.mutateAsync({
        username: user.username,
        displayName: profileData.displayName,
        bio: profileData.bio,
        avatar: profileData.avatar,
        email: user.email,
        socialLinks: socialLinksWithIds
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating profile:', error);
      alert('Failed to create profile. Please try again.');
    }
  };

  const availablePlatforms = platformOptions.filter(
    platform => !socialLinks.some(link => link.platform === platform.id)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto p-4 pt-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Step {step} of 3</span>
            <span className="text-sm text-gray-500">{Math.round((step / 3) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Let's set up your profile
              </h2>
              <p className="text-gray-600">
                Tell us about yourself and what you do
              </p>
            </div>

            <div className="space-y-6">
              {/* Avatar */}
              <div className="text-center">
                <div className="relative inline-block">
                  {profileData.avatar ? (
                    <img
                      src={profileData.avatar}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <button className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-2">Add a profile photo</p>
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your name or brand"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Tell people about yourself and what you do..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {profileData.bio.length}/200 characters
                </p>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!profileData.displayName || !profileData.bio}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg"
            >
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Add your social links
              </h2>
              <p className="text-gray-600">
                Connect all your social media profiles
              </p>
            </div>

            {/* Existing Links */}
            <div className="space-y-4">
              {socialLinks.map((link, index) => {
                const platform = platformOptions.find(p => p.id === link.platform);
                return (
                  <div key={index} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{platform?.icon}</span>
                        <span className="font-medium text-gray-900">{platform?.name}</span>
                      </div>
                      <button
                        onClick={() => removeSocialLink(index)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={platform?.placeholder}
                    />
                  </div>
                );
              })}
            </div>

            {/* Add New Link */}
            {availablePlatforms.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Add more platforms:</p>
                <div className="grid grid-cols-2 gap-3">
                  {availablePlatforms.map(platform => (
                    <button
                      key={platform.id}
                      onClick={() => addSocialLink(platform.id)}
                      className="flex items-center gap-2 p-3 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <span className="text-lg">{platform.icon}</span>
                      <span className="font-medium text-gray-700">{platform.name}</span>
                      <Plus className="w-4 h-4 text-gray-400 ml-auto" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 border border-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                You're all set! 🎉
              </h2>
              <p className="text-gray-600">
                Your profile is ready. You can always edit it later.
              </p>
            </div>

            {/* Profile Preview */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="text-center space-y-4">
                {profileData.avatar ? (
                  <img
                    src={profileData.avatar}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover mx-auto"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto">
                    <Camera className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{profileData.displayName}</h3>
                  <p className="text-blue-600 font-medium">@{user?.username}</p>
                  <p className="text-gray-600 mt-2">{profileData.bio}</p>
                </div>

                {socialLinks.length > 0 && (
                  <div className="space-y-2">
                    {socialLinks.slice(0, 2).map((link, index) => {
                      const platform = platformOptions.find(p => p.id === link.platform);
                      return (
                        <div key={index} className="flex items-center justify-center gap-2 text-sm text-gray-600">
                          <span>{platform?.icon}</span>
                          <span>{platform?.name}</span>
                          <ExternalLink className="w-3 h-3" />
                        </div>
                      );
                    })}
                    {socialLinks.length > 2 && (
                      <p className="text-sm text-gray-500">+{socialLinks.length - 2} more</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 border border-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleComplete}
                disabled={createUserProfileMutation.isPending}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg disabled:opacity-50"
              >
                {createUserProfileMutation.isPending ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating Profile...
                  </div>
                ) : (
                  'Complete Setup'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}